import React, { createContext, useContext } from 'react';
import { createPortal } from 'react-dom';
import { Box } from './legacy-layout';
import { splitStyleProps } from './style-props';
import { cn } from './utils';

const ModalContext = createContext(null);

export function Modal({
  isOpen,
  onClose,
  children,
  isCentered = false,
  size,
  closeOnEsc = true,
  closeOnOverlayClick = true
}) {
  React.useEffect(() => {
    if (!isOpen || !closeOnEsc) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, closeOnEsc, onClose]);

  return (
    <ModalContext.Provider
      value={{ isOpen, onClose, isCentered, size, closeOnOverlayClick }}
    >
      {children}
    </ModalContext.Provider>
  );
}

export function ModalOverlay(props) {
  const ctx = useContext(ModalContext);
  if (!ctx?.isOpen) return null;
  return createPortal(
    <div
      className="fixed inset-0 z-[1000] bg-black/50"
      onClick={ctx.closeOnOverlayClick ? ctx.onClose : undefined}
      {...props}
    />,
    document.body
  );
}

export function ModalContent({ children, className, ...props }) {
  const ctx = useContext(ModalContext);
  const { style, rest } = splitStyleProps(props);
  if (!ctx?.isOpen) return null;

  const sizeClass =
    typeof ctx.size === 'string'
      ? ctx.size === 'full'
        ? 'max-w-full h-full'
        : 'max-w-lg'
      : 'max-w-lg';

  return createPortal(
    <div
      className={cn(
        'fixed z-[1001] w-[calc(100%-2rem)] rounded-lg border bg-white p-6 shadow-lg',
        ctx.isCentered ? 'left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2' : 'left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2',
        sizeClass,
        className
      )}
      style={style}
      onClick={(event) => event.stopPropagation()}
      {...rest}
    >
      {children}
    </div>,
    document.body
  );
}

export function ModalHeader(props) {
  return <Box fontSize="lg" fontWeight="600" mb={4} {...props} />;
}

export function ModalBody(props) {
  return <Box {...props} />;
}

export function ModalFooter(props) {
  return <Box display="flex" justifyContent="flex-end" gap={3} mt={4} {...props} />;
}

export function ModalCloseButton({ onClick, ...props }) {
  const ctx = useContext(ModalContext);
  return (
    <button
      type="button"
      aria-label="Close"
      onClick={onClick || ctx?.onClose}
      className="absolute right-4 top-4 rounded-sm opacity-70 hover:opacity-100"
      {...props}
    >
      ×
    </button>
  );
}

const DrawerContext = createContext(null);

export function Drawer({ isOpen, onClose, children, placement = 'right' }) {
  return (
    <DrawerContext.Provider value={{ isOpen, onClose, placement }}>
      {children}
    </DrawerContext.Provider>
  );
}

export function DrawerOverlay() {
  const ctx = useContext(DrawerContext);
  if (!ctx?.isOpen) return null;
  return createPortal(
    <div className="fixed inset-0 z-[1000] bg-black/40" onClick={ctx.onClose} />,
    document.body
  );
}

export function DrawerContent({ children, ...props }) {
  const ctx = useContext(DrawerContext);
  const { style, rest } = splitStyleProps(props);
  if (!ctx?.isOpen) return null;

  const placementClass =
    ctx.placement === 'left'
      ? 'left-0 top-0 h-full'
      : 'right-0 top-0 h-full';

  return createPortal(
    <div
      className={cn('fixed z-[1001] w-full max-w-md overflow-y-auto bg-white shadow-xl', placementClass)}
      style={style}
      onClick={(event) => event.stopPropagation()}
      {...rest}
    >
      {children}
    </div>,
    document.body
  );
}

export function DrawerBody(props) {
  return <Box p={4} {...props} />;
}

export function DrawerHeader(props) {
  return <Box p={4} fontWeight="600" borderBottom="1px solid #e2e8f0" {...props} />;
}

export function DrawerCloseButton({ onClick, ...props }) {
  const ctx = useContext(DrawerContext);
  return (
    <button
      type="button"
      aria-label="Close drawer"
      onClick={onClick || ctx?.onClose}
      className="absolute right-4 top-4 rounded-sm opacity-70 hover:opacity-100"
      {...props}
    >
      ×
    </button>
  );
}

const AlertDialogCtx = createContext(null);

export function AlertDialog({ isOpen, onClose, leastDestructiveRef, children }) {
  return (
    <AlertDialogCtx.Provider value={{ isOpen, onClose, leastDestructiveRef }}>
      {children}
    </AlertDialogCtx.Provider>
  );
}

export function AlertDialogOverlay() {
  const ctx = useContext(AlertDialogCtx);
  if (!ctx?.isOpen) return null;
  return createPortal(<div className="fixed inset-0 z-[1000] bg-black/50" />, document.body);
}

export function AlertDialogContent({ children, ...props }) {
  const ctx = useContext(AlertDialogCtx);
  if (!ctx?.isOpen) return null;
  return createPortal(
    <div
      className="fixed left-1/2 top-1/2 z-[1001] w-[calc(100%-2rem)] max-w-lg -translate-x-1/2 -translate-y-1/2 rounded-lg border bg-white p-6 shadow-lg"
      {...props}
    >
      {children}
    </div>,
    document.body
  );
}

export function AlertDialogHeader(props) {
  return <Box mb={3} {...props} />;
}

export function AlertDialogBody(props) {
  return <Box mb={4} {...props} />;
}

export function AlertDialogFooter({ children, ...props }) {
  return (
    <Box display="flex" justifyContent="flex-end" gap={3} {...props}>
      {children}
    </Box>
  );
}

export function AlertDialogCloseButton(props) {
  const ctx = useContext(AlertDialogCtx);
  return <button type="button" onClick={ctx?.onClose} {...props} />;
}
